import React, { useMemo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Checkbox, Input, SearchIcon } from '@attentive-platform/stem-ui';

import { ArrowDownIcon, ArrowUpIcon } from '../rosettaSvgs';
import { SCOPE_TYPE_ENUM } from '../../../Constants/Constants';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { ScopeCategory, CustomRequirement, HighlightedText } from './TradeScopeComponents';

const PricingBox = ({ label, price, colorClass }) => {
  return (
    <span className={`${colorClass} font-medium`}>
      {label}: ${price?.toFixed(0) || 0}
    </span>
  );
};

const PricingReferenceStrip = ({ trade }) => (
  <div className='flex items-center justify-between gap-4 px-3 py-2 bg-amber-50 text-sm border-b border-amber-100'>
    <PricingBox label='Min' price={trade.base_price_per_sheet} colorClass='text-green-600' />
    <PricingBox label='Max' price={trade.max_basic_price_per_sheet} colorClass='text-amber-600' />
    <PricingBox label='Adv' price={trade.max_advanced_price_per_sheet} colorClass='text-red-500' />
  </div>
);

const TradesPanel = () => {
  const {
    tradesData,
    selectedTrades,
    expandedTrades,
    selectedScopes,
    customScopes,
    showPricingReference,
    searchQuery,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      expandedTrades: state.expandedTrades,
      selectedScopes: state.selectedScopes,
      customScopes: state.customScopes,
      showPricingReference: state.showPricingReference,
      searchQuery: state.searchQuery,
      dispatch: state.dispatch
    }))
  );

  // Filter trades and scopes based on search query
  const { filteredTrades, matchingTradeIds } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { filteredTrades: tradesData, matchingTradeIds: new Set() };
    }

    const query = searchQuery.toLowerCase().trim();
    const matchingIds = new Set();

    const filtered = tradesData
      .map(trade => {
        const tradeNameMatches = trade.trade_name.toLowerCase().includes(query);

        // Filter scopes that match the query (by name or description)
        const matchingScopes = trade.scopes.filter(scope => {
          const nameMatches = scope.scope_name.toLowerCase().includes(query);
          const descMatches = scope.scope_description?.toLowerCase().includes(query);
          return nameMatches || descMatches;
        });

        // Include trade if trade name matches or any scope matches
        if (tradeNameMatches || matchingScopes.length > 0) {
          matchingIds.add(trade.trade_id);
          return {
            ...trade,
            // If trade name matches, show all scopes; otherwise show only matching scopes
            scopes: tradeNameMatches ? trade.scopes : matchingScopes
          };
        }
        return null;
      })
      .filter(Boolean);

    return { filteredTrades: filtered, matchingTradeIds: matchingIds };
  }, [tradesData, searchQuery]);

  const handleSearchChange = e => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value });
  };

  const toggleTradeSelection = tradeId => {
    dispatch({ type: 'TOGGLE_TRADE_SELECTION', payload: tradeId });
  };

  const toggleTradeDropdown = tradeId => {
    dispatch({ type: 'TOGGLE_TRADE_DROPDOWN', payload: tradeId });
  };

  const tradesMap = useMemo(() => {
    const map = new Map();
    tradesData.forEach(trade => map.set(trade.trade_id, trade));
    return map;
  }, [tradesData]);

  const toggleScopeSelection = useCallback(
    (tradeId, scopeId) => {
      const trade = tradesMap.get(tradeId);
      const scope = trade?.scopes.find(s => s.scope_id === scopeId);

      if (scope?.is_default_selected) {
        return;
      }

      dispatch({ type: 'TOGGLE_SCOPE_SELECTION', payload: { tradeId, scopeId } });
    },
    [tradesMap, dispatch]
  );

  const handleScopeItemClick = scope => {
    dispatch({ type: 'SET_SCOPE_ITEM_CLICK', payload: { scope } });
  };

  const updateCustomScope = (tradeId, value) => {
    dispatch({ type: 'UPDATE_CUSTOM_SCOPE', payload: { tradeId, value } });
  };

  const handleCustomScopeChange = (tradeId, e) => {
    updateCustomScope(tradeId, e.target.value);
  };

  const handleSelectAll = (tradeId, scopes) => {
    const visibleScopeIds = scopes.map(s => s.scope_id);
    dispatch({ type: 'SELECT_ALL_VISIBLE_SCOPES', payload: { tradeId, visibleScopeIds } });
  };

  const handleDeselectAll = (tradeId, scopes) => {
    const visibleScopeIds = scopes.map(s => s.scope_id);
    const defaultScopeIds = scopes.filter(s => s.is_default_selected).map(s => s.scope_id);
    dispatch({ type: 'DESELECT_ALL_VISIBLE_SCOPES', payload: { tradeId, visibleScopeIds, defaultScopeIds } });
  };

  // Auto-expand trades with search matches
  const effectiveExpandedTrades = useMemo(() => {
    if (searchQuery.trim()) {
      return new Set([...expandedTrades, ...matchingTradeIds]);
    }
    return expandedTrades;
  }, [expandedTrades, matchingTradeIds, searchQuery]);

  const startIcon = { icon: () => <SearchIcon className='rosetta-search-icon' /> };

  return (
    <aside className='rosetta-panel'>
      <div className='rosetta-panel-header'>
        <h2>Trades & Scope</h2>
      </div>
      <div className='rosetta-search-container'>
        <Input
          size='small'
          value={searchQuery}
          startIcon={startIcon}
          onChange={handleSearchChange}
          className='rosetta-search-input'
          placeholder='Search trades or items...'
        />
      </div>
      <div className='rosetta-panel-content'>
        {filteredTrades.length === 0 && searchQuery.trim() && (
          <div className='rosetta-no-results'>No trades or items match "{searchQuery}"</div>
        )}
        {filteredTrades.map(trade => (
          <div key={trade.trade_id} className='rosetta-trade-item'>
            <div className='rosetta-trade-header' onClick={() => toggleTradeDropdown(trade.trade_id)}>
              <Checkbox
                checked={selectedTrades.has(trade.trade_id)}
                onChange={() => toggleTradeSelection(trade.trade_id)}
                size='small'
              />
              <div className='rosetta-trade-name'>
                <HighlightedText text={trade.trade_name} searchQuery={searchQuery} />
              </div>
              <div className='rosetta-trade-arrow'>
                {effectiveExpandedTrades.has(trade.trade_id) ? <ArrowDownIcon /> : <ArrowUpIcon />}
              </div>
            </div>

            {effectiveExpandedTrades.has(trade.trade_id) && (
              <>
                {showPricingReference && <PricingReferenceStrip trade={trade} />}

                <div className='rosetta-scope-items'>
                  <ScopeCategory
                    title='BASIC SCOPE'
                    tradeId={trade.trade_id}
                    searchQuery={searchQuery}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onToggleScope={toggleScopeSelection}
                    onScopeItemClick={handleScopeItemClick}
                    selectedScopes={selectedScopes[trade.trade_id]}
                    scopes={trade.scopes.filter(scope => scope.scope_type === SCOPE_TYPE_ENUM.BASIC)}
                  />

                  <ScopeCategory
                    title='ADVANCED SCOPE'
                    tradeId={trade.trade_id}
                    searchQuery={searchQuery}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onToggleScope={toggleScopeSelection}
                    onScopeItemClick={handleScopeItemClick}
                    selectedScopes={selectedScopes[trade.trade_id]}
                    scopes={trade.scopes.filter(scope => scope.scope_type === SCOPE_TYPE_ENUM.ADVANCED)}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Company-Specific Requirements Section */}
      <div className='rosetta-custom-scope-section'>
        <h3>Company Specific Requirements</h3>
        <div className='rosetta-custom-scope-container'>
          {selectedTrades.size === 0 ? (
            <p className='rosetta-custom-scope-placeholder'>Select trades to add custom requirements</p>
          ) : (
            Array.from(selectedTrades).map(tradeId => {
              const trade = tradesMap.get(tradeId);
              if (!trade) return null;

              return (
                <CustomRequirement
                  key={tradeId}
                  trade={trade}
                  customScope={customScopes[tradeId] || ''}
                  onChange={e => handleCustomScopeChange(tradeId, e)}
                />
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
};

export default TradesPanel;
